import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useCategories, type Category, type HierarchicalCategory } from '@/hooks/use-categories';

const ICONS = [
  { value: 'CircleDot', label: 'Padrão' },
  { value: 'ShoppingCart', label: 'Compras' },
  { value: 'Utensils', label: 'Alimentação' },
  { value: 'Car', label: 'Transporte' },
  { value: 'Home', label: 'Casa' },
  { value: 'Heart', label: 'Saúde' },
  { value: 'GraduationCap', label: 'Educação' },
  { value: 'Gamepad2', label: 'Lazer' },
  { value: 'Briefcase', label: 'Trabalho' },
  { value: 'Wallet', label: 'Finanças' },
  { value: 'Gift', label: 'Presentes' },
  { value: 'Plane', label: 'Viagem' },
  { value: 'Smartphone', label: 'Tecnologia' },
  { value: 'Shirt', label: 'Vestuário' },
  { value: 'Baby', label: 'Filhos' },
  { value: 'PawPrint', label: 'Pets' },
];

const COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308',
  '#84cc16', '#22c55e', '#10b981', '#14b8a6',
  '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1',
  '#8b5cf6', '#a855f7', '#d946ef', '#ec4899',
];

// Legacy interface for backward compatibility with CategorySelect
interface LegacyCategoryCreateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: 'income' | 'expense';
  parentCategories: HierarchicalCategory[];
  onCreate: (data: {
    name: string;
    type: 'income' | 'expense';
    icon?: string;
    color?: string;
    parent_id?: string | null;
  }) => Promise<Category | null>;
  defaultParentId?: string | null;
}

// New interface for standalone usage
interface StandaloneCategoryCreateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  defaultParentId?: string;
  defaultType?: 'income' | 'expense';
}

type CategoryCreateModalProps = LegacyCategoryCreateModalProps | StandaloneCategoryCreateModalProps;

function isLegacyProps(props: CategoryCreateModalProps): props is LegacyCategoryCreateModalProps {
  return 'onCreate' in props && 'parentCategories' in props;
}

export function CategoryCreateModal(props: CategoryCreateModalProps) {
  const { open, onOpenChange, defaultParentId = null } = props;
  
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState<'income' | 'expense'>(
    isLegacyProps(props) ? props.type : (props.defaultType || 'expense')
  );
  const [icon, setIcon] = useState('CircleDot');
  const [color, setColor] = useState('#6366f1');
  const [parentId, setParentId] = useState<string | null>(defaultParentId || null);

  // For standalone mode, fetch categories
  const { hierarchicalCategories, createCategory } = useCategories('all');

  const parentCategories = isLegacyProps(props) 
    ? props.parentCategories 
    : hierarchicalCategories.filter(c => c.type === type);

  useEffect(() => {
    if (defaultParentId) {
      setParentId(defaultParentId);
    }
    if (!isLegacyProps(props) && props.defaultType) {
      setType(props.defaultType);
    }
  }, [defaultParentId, props]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setLoading(true);
    
    let result: Category | null = null;
    
    if (isLegacyProps(props)) {
      result = await props.onCreate({
        name: name.trim(),
        type,
        icon,
        color,
        parent_id: parentId
      });
    } else {
      result = await createCategory({
        name: name.trim(),
        type,
        icon,
        color,
        parent_id: parentId
      });
      
      if (result && props.onSuccess) {
        props.onSuccess();
      }
    }

    setLoading(false);

    if (result) {
      setName('');
      setIcon('CircleDot');
      setColor('#6366f1');
      setParentId(null);
    }
  };

  const showTypeSelector = !isLegacyProps(props);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Nova {parentId ? 'Subcategoria' : 'Categoria'}
            {!showTypeSelector && ` de ${type === 'income' ? 'Receita' : 'Despesa'}`}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome *</Label>
            <Input
              id="name"
              placeholder="Ex: Supermercado"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
            />
          </div>

          {showTypeSelector && (
            <div className="space-y-2">
              <Label>Tipo *</Label>
              <Select value={type} onValueChange={(v) => {
                setType(v as 'income' | 'expense');
                setParentId(null); // Reset parent when type changes
              }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="expense">Despesa</SelectItem>
                  <SelectItem value="income">Receita</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label>Categoria Pai (opcional)</Label>
            <Select value={parentId || 'none'} onValueChange={(v) => setParentId(v === 'none' ? null : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Nenhuma (categoria principal)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhuma (categoria principal)</SelectItem>
                {parentCategories
                  .filter(c => c.type === type)
                  .map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Ícone</Label>
            <Select value={icon} onValueChange={setIcon}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ICONS.map(i => (
                  <SelectItem key={i.value} value={i.value}>
                    {i.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Cor</Label>
            <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
              {COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    color === c ? 'border-foreground scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading || !name.trim()} className="flex-1">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Criando...
                </>
              ) : (
                'Criar'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
